import type { RefObject } from 'react';
import { Button } from '@ooh/ui/components/button';
import { Pagination } from '@ooh/ui/components/pagination';
import { ClientOfferReviewUnitCard } from './client-offer-review-unit-card';
import type {
  ClientOfferReviewDecision,
  ClientOfferReviewPeriod,
  ClientOfferReviewPhotoMode,
  ClientOfferReviewUnit,
  ClientOfferReviewUnitListLabels,
} from './client-offer-review-types';

interface ClientOfferReviewUnitListProps {
  listRef: RefObject<HTMLDivElement | null>;
  rowRefs: RefObject<Record<string, HTMLDivElement | null>>;
  visibleRows: ClientOfferReviewUnit[];
  /** Position of `visibleRows[0]` within the full filtered/sorted row list — used for the "lp/total" badge so it reflects the row's real rank, not its index within the current page. */
  startIndex: number;
  totalUnitCount: number;
  decisions: Record<string, ClientOfferReviewDecision>;
  selectedPeriods: Record<string, string[]>;
  activeId: string | null;
  pulseId: string | null;
  photoMode: ClientOfferReviewPhotoMode;
  periods: ClientOfferReviewPeriod[];
  readOnly: boolean;
  roHint: string;
  onLocate: (id: string) => void;
  onOpenDetail: (id: string) => void;
  onOpenLightbox: (id: string) => void;
  onAccept: (unit: ClientOfferReviewUnit) => void;
  onReject: (unit: ClientOfferReviewUnit) => void;
  /** Toggles a free chip on the given unit's period strip. */
  onTogglePeriod?: (unitId: string, periodId: string, nowSelected: boolean) => void;
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
  noResults: boolean;
  onClearFilters: () => void;
  labels: ClientOfferReviewUnitListLabels;
}

/** Scrolling unit card list — numbered pagination bar + empty state. */
export function ClientOfferReviewUnitList(props: ClientOfferReviewUnitListProps) {
  const {
    listRef,
    rowRefs,
    visibleRows,
    startIndex,
    totalUnitCount,
    decisions,
    selectedPeriods,
    activeId,
    pulseId,
    photoMode,
    periods,
    readOnly,
    roHint,
    onLocate,
    onOpenDetail,
    onOpenLightbox,
    onAccept,
    onReject,
    onTogglePeriod,
    page,
    pageCount,
    onPageChange,
    noResults,
    onClearFilters,
    labels,
  } = props;

  return (
    <div
      ref={listRef}
      data-co-list
      data-testid="offer-review-list"
      className="flex h-full min-h-0 flex-col gap-2 overflow-y-auto pb-20 pr-1"
    >
      {visibleRows.map((unit, i) => (
        <ClientOfferReviewUnitCard
          key={unit.id}
          unit={unit}
          lp={String(startIndex + i + 1).padStart(2, '0')}
          totalCount={totalUnitCount}
          decision={decisions[unit.id] ?? null}
          photoMode={photoMode}
          periods={periods}
          selectedPeriodIds={selectedPeriods[unit.id] ?? []}
          active={activeId === unit.id}
          pulsing={pulseId === unit.id}
          readOnly={readOnly}
          roHint={roHint}
          onLocate={() => onLocate(unit.id)}
          onOpenDetail={() => onOpenDetail(unit.id)}
          onOpenLightbox={() => onOpenLightbox(unit.id)}
          onAccept={() => onAccept(unit)}
          onReject={() => onReject(unit)}
          onTogglePeriod={
            onTogglePeriod
              ? (periodId, nowSelected) => onTogglePeriod(unit.id, periodId, nowSelected)
              : undefined
          }
          rowRef={(el) => {
            rowRefs.current[unit.id] = el;
          }}
          labels={labels.unitCard}
        />
      ))}

      {!noResults ? (
        <Pagination
          page={page}
          pageCount={pageCount}
          onPageChange={onPageChange}
          labels={labels.pagination}
          className="shrink-0 py-2"
        />
      ) : null}

      {noResults ? (
        <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border px-6 py-14 text-center">
          <span className="text-sm font-semibold">{labels.noResultsTitle}</span>
          <Button
            type="button"
            variant="outline"
            onClick={onClearFilters}
            data-testid="clear-filters-button"
            className="mt-1 h-8 rounded-lg border-border px-4 text-xs font-medium"
          >
            {labels.clearFilters}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
